import { useState, useMemo, useEffect } from "react";
import { usePagination } from "@/hooks/usePagination";
import { PaginationBar } from "@/components/PaginationBar";
import { useWindowedList } from "@/hooks/useWindowedList";
import { Link, useNavigate } from "react-router-dom";
import { EmptyState } from "@/components/EmptyState";
import { useAppData } from "@/data/AppDataContext";
import { useAuth } from "@/contexts/AuthContext";
import { getCurrentRepName } from "@/lib/getCurrentRepName";
import { useRole } from "@/data/RoleContext";
import { QuoteService } from "@/services/quote.service";
import { LeadService } from "@/services/lead.service";
import { toUiQuote } from "@/types/mappers/quote.mapper";
import type { QuoteStatus } from "@/data/quoteTypes";
import { isActiveStatus, isClosedStatus } from "@/data/quoteTypes";
import { getAllOpportunities, getOpportunityStats } from "@/data/opportunitySelectors";
import { formatStandardTimestamp, formatShortDate } from "@/lib/dateFormat";
import { fetchProfileDisplayNames } from "@/lib/profileLookupCache";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { formatPhoneNumber } from "@/lib/phoneFormat";
import {
  Plus, Search, Clock, CheckCircle, XCircle, ArrowRight, Target,
  Users, AlertTriangle, TrendingUp, DollarSign, CalendarClock, Filter,
} from "lucide-react";
import { useActionPermissions } from "@/hooks/useActionPermissions";
import { toast } from "sonner";

/* ── Status Config ── */
const STATUS_CONFIG: Record<QuoteStatus, { icon: React.ElementType; color: string }> = {
  "Lead": { icon: Target, color: "bg-info/10 text-info border-info/30" },
  "Quote Created": { icon: Target, color: "bg-primary/10 text-primary border-primary/30" },
  "Appointment Scheduled": { icon: Clock, color: "bg-warning/10 text-warning border-warning/30" },
  "Appointment Completed": { icon: Target, color: "bg-primary/10 text-primary border-primary/30" },
  "Follow-Up Needed": { icon: Clock, color: "bg-warning/10 text-warning border-warning/30" },
  "Closed Won": { icon: CheckCircle, color: "bg-success/10 text-success border-success/30" },
  "Closed Lost": { icon: XCircle, color: "bg-destructive/10 text-destructive border-destructive/30" },
};

const ALL_STATUSES: QuoteStatus[] = ["Lead", "Quote Created", "Appointment Scheduled", "Appointment Completed", "Follow-Up Needed", "Closed Won", "Closed Lost"];

/* ── Disposition reasons (for display/filter) ── */
const DISPOSITION_REASONS = [
  "Price too high",
  "Need to think about it",
  "Comparing competitors",
  "No decision maker present",
  "Timing not right",
  "Project postponed",
  "Financing concerns",
  "Other",
] as const;

/* ── Roles that see all data ── */
const GLOBAL_ROLES = ["super_admin", "admin", "owner", "ops_manager", "cfo", "finance_manager", "it_staff"];

export default function QuotesList() {
  const { jobs } = useAppData();
  const { role } = useRole();
  const { user } = useAuth();
  const navigate = useNavigate();
  const actionPerms = useActionPermissions();
  const [quotesData, setQuotesData] = useState<ReturnType<typeof toUiQuote>[]>([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [repNameById, setRepNameById] = useState<Map<string, string>>(new Map());
  const [tableScrollTop, setTableScrollTop] = useState(0);

  /* ── Filters ── */
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [repFilter, setRepFilter] = useState<string>("all");
  const [dispositionFilter, setDispositionFilter] = useState<string>("all");
  const [projectTypeFilter, setProjectTypeFilter] = useState<string>("all");
  const [dateRange, setDateRange] = useState<string>("all");
  const [createOpen, setCreateOpen] = useState(false);
  const [newCustomer, setNewCustomer] = useState("");
  const [newEmail, setNewEmail] = useState("");
  const [newPhone, setNewPhone] = useState("");
  const [newAddress, setNewAddress] = useState("");
  const [newNotes, setNewNotes] = useState("");
  const [creating, setCreating] = useState(false);

  const currentRep = useMemo(() => getCurrentRepName(user), [user]);
  const normalizedCurrentRep = String(currentRep ?? "").trim().toLowerCase();
  const profileId = String(user?.id ?? "").trim();

  const isUuidLike = (value: string) =>
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i.test(value.trim());

  useEffect(() => {
    let cancelled = false;
    const loadQuotes = async () => {
      setInitialLoading(true);
      const { data, error } = await QuoteService.getAll();
      if (error) {
        console.error("Failed to load quotes:", error);
        if (!cancelled) setInitialLoading(false);
        return;
      }
      if (cancelled) return;
      const mapped = ((data ?? []) as any[]).map((row) => toUiQuote(row));
      setQuotesData(mapped);
      setInitialLoading(false);
    };
    void loadQuotes();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    const loadRepNames = async () => {
      const repIds = Array.from(
        new Set(
          quotesData
            .flatMap((q) => [q.assignedRep, q.createdBy])
            .filter((v): v is string => typeof v === "string" && isUuidLike(v)),
        ),
      );
      if (repIds.length === 0) {
        setRepNameById(new Map());
        return;
      }
      if (cancelled) return;
      const map = await fetchProfileDisplayNames(repIds);
      if (cancelled) return;
      setRepNameById(map);
    };
    void loadRepNames();
    return () => {
      cancelled = true;
    };
  }, [quotesData]);

  const getRepLabel = (q: (typeof quotesData)[number]) => {
    const assigned = String(q.assignedRep ?? "").trim();
    if (assigned && !isUuidLike(assigned)) return assigned;
    if (assigned && isUuidLike(assigned)) return repNameById.get(assigned) ?? "—";
    const created = String(q.createdBy ?? "").trim();
    if (created && isUuidLike(created)) return repNameById.get(created) ?? "—";
    if (created) return created;
    return "—";
  };

  /* ── Role-based data isolation ── */
  const visibleQuotes = useMemo(() => {
    const shouldScopeToOwnRep = role === "sales";
    if (!shouldScopeToOwnRep) return quotesData;
    return quotesData.filter((q) => {
      const assigned = String(q.assignedRep ?? "").trim();
      const createdBy = String(q.createdBy ?? "").trim();
      if (profileId && (assigned === profileId || createdBy === profileId)) return true;
      const repLabel = getRepLabel(q).toLowerCase();
      return normalizedCurrentRep.length > 0 && repLabel === normalizedCurrentRep;
    });
  }, [quotesData, role, profileId, normalizedCurrentRep, repNameById]);

  /* ── Derived lists for filter dropdowns ── */
  const allReps = useMemo(() => {
    const set = new Set(quotesData.map(getRepLabel).filter((v) => v && v !== "—"));
    return Array.from(set).sort();
  }, [quotesData, repNameById]);

  const allProjectTypes = useMemo(() => {
    const set = new Set(
      quotesData.map((q) => q.projectConfig?.projectCategory || q.projectConfig?.consultationCategory || "").filter(Boolean)
    );
    return Array.from(set).sort();
  }, [quotesData]);

  /* ── Date range helper ── */
  const isInDateRange = (dateStr: string) => {
    if (dateRange === "all") return true;
    const d = new Date(dateStr);
    const now = new Date();
    const diffDays = (now.getTime() - d.getTime()) / (1000 * 60 * 60 * 24);
    if (dateRange === "7") return diffDays <= 7;
    if (dateRange === "14") return diffDays <= 14;
    if (dateRange === "30") return diffDays <= 30;
    if (dateRange === "90") return diffDays <= 90;
    return true;
  };

  /* ── Filter logic ── */
  const filtered = useMemo(() => {
    return visibleQuotes.filter((q) => {
      const matchSearch =
        !search ||
        q.customerName.toLowerCase().includes(search.toLowerCase()) ||
        (q.displayReference || q.id).toLowerCase().includes(search.toLowerCase()) ||
        getRepLabel(q).toLowerCase().includes(search.toLowerCase());
      const matchStatus = statusFilter === "all" || q.stage === statusFilter;
      const matchRep = repFilter === "all" || getRepLabel(q) === repFilter;
      const matchDisposition = dispositionFilter === "all" || q.lostReason === dispositionFilter;
      const projectType = q.projectConfig?.projectCategory || q.projectConfig?.consultationCategory || "";
      const matchProject = projectTypeFilter === "all" || projectType === projectTypeFilter;
      const matchDate = isInDateRange(q.createdAt);
      return matchSearch && matchStatus && matchRep && matchDisposition && matchProject && matchDate;
    });
  }, [visibleQuotes, search, statusFilter, repFilter, dispositionFilter, projectTypeFilter, dateRange, repNameById]);
  const pagination = usePagination(filtered, 25);
  useEffect(() => {
    pagination.reset();
    setTableScrollTop(0);
  }, [search, statusFilter, repFilter, dispositionFilter, projectTypeFilter, dateRange]);
  const TABLE_VIEWPORT_HEIGHT = 640;
  const TABLE_ROW_HEIGHT = 48;
  const rowWindow = useWindowedList(pagination.paged, {
    itemHeight: TABLE_ROW_HEIGHT,
    viewportHeight: TABLE_VIEWPORT_HEIGHT,
    scrollTop: tableScrollTop,
    overscan: 10,
  });

  /* ── KPI Metrics ── */
  const metrics = useMemo(() => {
    const enriched = getAllOpportunities(visibleQuotes);
    const stats = getOpportunityStats(enriched);
    return {
      totalOpen: stats.openCount,
      totalFollowUp: stats.followUpCount,
      repFollowUps: visibleQuotes.filter((q) => q.stage === "Follow-Up Needed" && !q.lostReason?.includes("Manager")).length,
      managerFollowUps: visibleQuotes.filter((q) => q.stage === "Follow-Up Needed" && q.lostReason?.includes("Manager")).length,
      aging7: stats.aging7,
      aging14: stats.aging14,
      closeRate: stats.closeRate,
      pipelineValue: stats.pipelineValue,
    };
  }, [visibleQuotes]);

  /* ── Helpers ── */
  const getProjectType = (q: (typeof quotesData)[number]) =>
    q.projectConfig?.projectCategory || q.projectConfig?.consultationCategory || "—";

  const getDisposition = (q: (typeof quotesData)[number]) => q.lostReason || "—";

  const handleCreate = async () => {
    if (!newCustomer.trim() || creating) return;
    setCreating(true);
    try {
      const leadCreate = await LeadService.createWithCustomer({
        primary_name: newCustomer.trim(),
        primary_phone: newPhone.trim(),
        primary_email: newEmail.trim() || "",
        address: newAddress.trim(),
        city: "",
        state: "",
        zip: "",
        project_type: "Shower Remodel",
        lead_source: "Manual",
        assigned_rep: profileId || null,
        notes: newNotes.trim() || null,
      });
      if (leadCreate.error || !leadCreate.data) {
        throw new Error((leadCreate.error as { message?: string } | null)?.message || "Failed to create lead/customer");
      }
      const row = Array.isArray(leadCreate.data) ? leadCreate.data[0] : leadCreate.data;
      const leadId = String((row as any)?.lead_id ?? (row as any)?.id ?? "").trim();
      const customerId = String((row as any)?.customer_id ?? "").trim();
      if (!leadId) throw new Error("Lead creation returned no lead id");

      const quoteUpsert = await QuoteService.upsertByLeadId(leadId, {
        quote_name: newCustomer.trim(),
        customer_name: newCustomer.trim(),
        customer_email: newEmail.trim() || null,
        customer_phone: newPhone.trim() || null,
        customer_address: newAddress.trim() || null,
        customer_id: customerId || null,
        lead_source: "Manual",
        status: "Lead",
        notes: newNotes.trim() || null,
        total_estimate: 0,
        project_config: {
          consultationCategory: "Shower Remodel",
          projectCategory: "Shower Remodel",
          autoProjectType: "Shower Remodel",
        },
      });
      if (quoteUpsert.error || !quoteUpsert.data?.id) {
        throw new Error((quoteUpsert.error as { message?: string } | null)?.message || "Failed to create quote");
      }

      await LeadService.update(leadId, { linked_quote_id: quoteUpsert.data.id, stage: "Quote Created" });
      const { data: refreshedQuotes } = await QuoteService.getAll();
      const mapped = ((refreshedQuotes ?? []) as any[]).map((r) => toUiQuote(r));
      setQuotesData(mapped);

      setCreateOpen(false);
      setNewCustomer("");
      setNewEmail("");
      setNewPhone("");
      setNewAddress("");
      setNewNotes("");
      toast.success("Opportunity created");
      navigate(`/opportunities/${quoteUpsert.data.id}`);
    } catch (err) {
      console.error("Failed to create opportunity:", err);
      toast.error("Failed to create opportunity");
    } finally {
      setCreating(false);
    }
  };

  const showRepColumn = GLOBAL_ROLES.includes(role);

  return (
    <div className="space-y-4">
      {/* ── Header ── */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-xl font-bold tracking-tight">Opportunities</h1>
          <p className="text-xs text-muted-foreground mt-0.5">
            {visibleQuotes.length} total · {metrics.totalOpen} open · {metrics.totalFollowUp} follow-ups
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)} className="gap-1.5" size="sm" {...actionPerms.guardProps("create_opportunity")}>
          <Plus className="h-3.5 w-3.5" /> New Opportunity
        </Button>
      </div>

      {/* ── KPI Metrics Bar ── */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-2">
        <MetricCard label="Open" value={metrics.totalOpen} icon={Target} accent="text-info" />
        <MetricCard label="Follow-Up" value={metrics.totalFollowUp} icon={Clock} accent="text-warning" />
        <MetricCard label="Rep Follow-Ups" value={metrics.repFollowUps} icon={Users} accent="text-info" />
        <MetricCard label="Mgr Follow-Ups" value={metrics.managerFollowUps} icon={AlertTriangle} accent="text-warning" />
        <MetricCard label="Aging > 7d" value={metrics.aging7} icon={CalendarClock} accent={metrics.aging7 > 0 ? "text-warning" : "text-muted-foreground"} />
        <MetricCard label="Aging > 14d" value={metrics.aging14} icon={CalendarClock} accent={metrics.aging14 > 0 ? "text-destructive" : "text-muted-foreground"} />
        <MetricCard label="Close Rate" value={`${metrics.closeRate}%`} icon={TrendingUp} accent="text-success" />
        <MetricCard label="Pipeline Value" value={`$${(metrics.pipelineValue / 1000).toFixed(0)}k`} icon={DollarSign} accent="text-primary" />
      </div>

      {/* ── Filters ── */}
      <Card className="shadow-none">
        <CardContent className="p-3">
          <div className="flex items-center gap-2 flex-wrap">
            <Filter className="h-3.5 w-3.5 text-muted-foreground" />
            <div className="relative flex-1 min-w-[160px] max-w-xs">
              <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
              <Input placeholder="Search…" value={search} onChange={(e) => setSearch(e.target.value)} className="pl-8 h-7 text-xs" />
            </div>
            <Select value={statusFilter} onValueChange={setStatusFilter}>
              <SelectTrigger className="w-40 h-7 text-xs"><SelectValue placeholder="Status" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {ALL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
              </SelectContent>
            </Select>
            {showRepColumn && (
              <Select value={repFilter} onValueChange={setRepFilter}>
                <SelectTrigger className="w-36 h-7 text-xs"><SelectValue placeholder="Rep" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Reps</SelectItem>
                  {allReps.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
                </SelectContent>
              </Select>
            )}
            <Select value={dispositionFilter} onValueChange={setDispositionFilter}>
              <SelectTrigger className="w-44 h-7 text-xs"><SelectValue placeholder="Disposition" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Dispositions</SelectItem>
                {DISPOSITION_REASONS.map((r) => <SelectItem key={r} value={r}>{r}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={projectTypeFilter} onValueChange={setProjectTypeFilter}>
              <SelectTrigger className="w-36 h-7 text-xs"><SelectValue placeholder="Project Type" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {allProjectTypes.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
            <Select value={dateRange} onValueChange={setDateRange}>
              <SelectTrigger className="w-32 h-7 text-xs"><SelectValue placeholder="Date Range" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="7">Last 7 Days</SelectItem>
                <SelectItem value="14">Last 14 Days</SelectItem>
                <SelectItem value="30">Last 30 Days</SelectItem>
                <SelectItem value="90">Last 90 Days</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* ── Table ── */}
      <Card className="shadow-none">
        <CardContent
          className="p-0 overflow-x-auto overflow-y-auto"
          style={{ maxHeight: TABLE_VIEWPORT_HEIGHT }}
          onScroll={(event) => setTableScrollTop(event.currentTarget.scrollTop)}
        >
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b bg-muted/30">
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">ID</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Customer</th>
                {showRepColumn && <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Sales Rep</th>}
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Project Type</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Status</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Disposition</th>
                <th className="text-right px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Total Price</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Created</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Last Activity</th>
                <th className="text-left px-3 py-2 font-semibold text-muted-foreground whitespace-nowrap">Linked Job</th>
              </tr>
            </thead>
            <tbody>
              {rowWindow.topSpacerHeight > 0 && (
                <tr aria-hidden>
                  <td colSpan={showRepColumn ? 10 : 9} style={{ height: rowWindow.topSpacerHeight, padding: 0, border: 0 }} />
                </tr>
              )}
              {rowWindow.visibleItems.map((q) => {
                const cfg = STATUS_CONFIG[q.stage] || STATUS_CONFIG["Lead"];
                const Icon = cfg.icon;
                const now = new Date();
                const ageDays = Math.floor((now.getTime() - new Date(q.createdAt).getTime()) / (1000 * 60 * 60 * 24));
                const isAging = isActiveStatus(q.stage) && ageDays > 7;
                const isCriticalAge = isActiveStatus(q.stage) && ageDays > 14;
                return (
                  <tr key={q.id} className={`border-b last:border-0 hover:bg-muted/20 transition-colors ${isCriticalAge ? "bg-destructive/5" : isAging ? "bg-warning/5" : ""}`}>
                    <td className="px-3 py-2.5">
                      <Link to={`/opportunities/${q.id}`} className="font-medium text-primary hover:underline">
                        {q.displayReference || q.id}
                      </Link>
                    </td>
                    <td className="px-3 py-2.5 font-medium max-w-[140px] truncate">{q.customerName}</td>
                    {showRepColumn && <td className="px-3 py-2.5 text-muted-foreground">{getRepLabel(q)}</td>}
                    <td className="px-3 py-2.5 text-muted-foreground">{getProjectType(q)}</td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-md border text-[10px] font-semibold ${cfg.color}`}>
                        <Icon className="h-3 w-3" /> {q.stage}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      {q.lostReason ? (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className="text-muted-foreground max-w-[120px] truncate block cursor-default">{q.lostReason}</span>
                            </TooltipTrigger>
                            <TooltipContent><p className="text-xs max-w-[200px]">{q.lostReason}</p></TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                    <td className="px-3 py-2.5 text-right font-medium">
                      {q.totalEstimate > 0 ? `$${q.totalEstimate.toLocaleString()}` : "—"}
                    </td>
                    <td className="px-3 py-2.5">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground cursor-default">{formatShortDate(q.createdAt)}</span>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">{formatStandardTimestamp(q.createdAt)}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-3 py-2.5">
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <span className="text-muted-foreground cursor-default">{formatShortDate(q.updatedAt)}</span>
                          </TooltipTrigger>
                          <TooltipContent><p className="text-xs">{formatStandardTimestamp(q.updatedAt)}</p></TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </td>
                    <td className="px-3 py-2.5">
                      {(() => {
                        const jobRef = String(q.convertedJobId || q.linkedJobId || "").trim();
                        if (!jobRef) return <span className="text-muted-foreground">—</span>;
                        const matched = jobs.find((j) => j.id === jobRef || String((j as any).jobNumber ?? "").trim() === jobRef);
                        const jobId = matched?.id ?? (isUuidLike(jobRef) ? jobRef : "");
                        if (!jobId) return <span className="text-muted-foreground">—</span>;
                        return (
                          <Link to={`/jobs/${jobId}`} className="text-primary hover:underline inline-flex items-center gap-1">
                            {matched?.jobNumber || jobRef} <ArrowRight className="h-3 w-3" />
                          </Link>
                        );
                      })()}
                    </td>
                  </tr>
                );
              })}
              {rowWindow.bottomSpacerHeight > 0 && (
                <tr aria-hidden>
                  <td colSpan={showRepColumn ? 10 : 9} style={{ height: rowWindow.bottomSpacerHeight, padding: 0, border: 0 }} />
                </tr>
              )}
              {!initialLoading && filtered.length === 0 && (
                <tr><td colSpan={showRepColumn ? 10 : 9} className="p-0">
                  <EmptyState
                    icon={Target}
                    title="No opportunities yet"
                    description="Create your first opportunity to start tracking the sales pipeline."
                    actionLabel="New Opportunity"
                    onAction={() => setCreateOpen(true)}
                  />
                </td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>
      {initialLoading && (
        <Card className="shadow-none">
          <CardContent className="p-6">
            <EmptyState icon={Clock} text="Loading latest opportunities..." spinning />
          </CardContent>
        </Card>
      )}
      <PaginationBar {...pagination} />

      {/* Results summary */}
      <p className="text-[10px] text-muted-foreground text-right">
        Showing {filtered.length} of {visibleQuotes.length} opportunities
      </p>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Target className="h-4 w-4" /> New Opportunity</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs">Customer Name *</Label>
              <Input value={newCustomer} onChange={(e) => setNewCustomer(e.target.value)} placeholder="Full name" className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Email</Label>
                <Input value={newEmail} onChange={(e) => setNewEmail(e.target.value)} placeholder="email@example.com" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs">Phone</Label>
                <Input value={newPhone} onChange={(e) => setNewPhone(formatPhoneNumber(e.target.value))} placeholder="555-000-0000" className="mt-1" />
              </div>
            </div>
            <div>
              <Label className="text-xs">Address</Label>
              <Input value={newAddress} onChange={(e) => setNewAddress(e.target.value)} placeholder="Full address" className="mt-1" />
            </div>
            <div>
              <Label className="text-xs">Notes</Label>
              <Textarea value={newNotes} onChange={(e) => setNewNotes(e.target.value)} placeholder="Initial notes…" className="mt-1" rows={2} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button onClick={handleCreate} disabled={!newCustomer.trim() || creating}>Create Opportunity</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ── KPI Metric Card ── */
function MetricCard({ label, value, icon: Icon, accent }: { label: string; value: string | number; icon: React.ElementType; accent: string }) {
  return (
    <Card className="shadow-none">
      <CardContent className="p-2.5 flex flex-col items-center text-center gap-0.5">
        <Icon className={`h-3.5 w-3.5 ${accent}`} />
        <span className="text-base font-bold leading-tight">{value}</span>
        <span className="text-[9px] text-muted-foreground leading-tight">{label}</span>
      </CardContent>
    </Card>
  );
}
